import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FileSpreadsheet,
  Handshake,
  Calculator,
  Download,
  MousePointer,
  Users,
  TrendingUp,
  ArrowUpRight,
  Percent,
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

interface AdminStats {
  templates: number;
  templateDownloads: number;
  templateTrend: string;
  partners: number;
  partnerClicks: number;
  partnerTrend: string;
  dealTemplates: number;
  dealUsage: number;
  dealTrend: string;
  conversionRate: string;
}

interface RecentActivity {
  id: string;
  action: string;
  item: string;
  user: string;
  time: string;
  type: "template" | "partner" | "deal";
}

export default function AdminOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats>({
    templates: 0,
    templateDownloads: 0,
    templateTrend: "+0%",
    partners: 0,
    partnerClicks: 0,
    partnerTrend: "+0%",
    dealTemplates: 0,
    dealUsage: 0,
    dealTrend: "+0%",
    conversionRate: "0%",
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();

    const subscription = supabase
      .channel("admin-dashboard")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "activity_logs" },
        () => {
          fetchRecentActivity();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTemplateStats(),
        fetchPartnerStats(),
        fetchRecentActivity(),
      ]);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateStats = async () => {
    try {
      const { data: templates, error: templatesError } = await supabase
        .from("templates")
        .select("*");

      if (templatesError) throw templatesError;

      const { data: downloadsData, error: downloadsError } = await supabase
        .from("template_downloads")
        .select("*");

      if (downloadsError) throw downloadsError;

      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);

      const { data: lastWeekDownloadsData, error: lastWeekError } =
        await supabase
          .from("template_downloads")
          .select("*")
          .gte("downloaded_at", lastWeek.toISOString());

      if (lastWeekError) throw lastWeekError;

      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data: prevWeekDownloadsData } = await supabase
        .from("template_downloads")
        .select("*")
        .gte("downloaded_at", twoWeeksAgo.toISOString())
        .lt("downloaded_at", oneWeekAgo.toISOString());

      const currentWeekDownloads = lastWeekDownloadsData?.length || 0;
      const prevWeekDownloads = prevWeekDownloadsData?.length || 0;

      let templateTrend = "+0%";
      if (prevWeekDownloads > 0) {
        const percentageChange =
          ((currentWeekDownloads - prevWeekDownloads) / prevWeekDownloads) *
          100;
        templateTrend = `${percentageChange > 0 ? "+" : ""}${Math.round(percentageChange)}%`;
      } else if (currentWeekDownloads > 0) {
        templateTrend = "+100%";
      }

      setStats((prev) => ({
        ...prev,
        templates: templates?.length || 0,
        templateDownloads: downloadsData?.length || 0,
        templateTrend,
      }));
    } catch (error) {
      console.error("Error fetching template stats:", error);
    }
  };

  const fetchPartnerStats = async () => {
    try {
      const { data: partners, error: partnersError } = await supabase
        .from("partners")
        .select("*");

      if (partnersError) throw partnersError;

      const partnerClicks =
        partners?.reduce(
          (sum, partner) => sum + (partner.website_clicks || 0),
          0
        ) || 0;

      const introRequests =
        partners?.reduce(
          (sum, partner) => sum + (partner.intro_requests || 0),
          0
        ) || 0;

      const totalViews =
        partners?.reduce((sum, partner) => sum + (partner.views || 0), 0) || 0;

      const conversionRate =
        totalViews > 0
          ? ((introRequests / totalViews) * 100).toFixed(1) + "%"
          : "0%";

      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);

      const { data: lastWeekActivity, error: activityError } = await supabase
        .from("activity_logs")
        .select("*")
        .gte("timestamp", lastWeek.toISOString());

      if (activityError) throw activityError;

      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data: prevWeekActivity } = await supabase
        .from("activity_logs")
        .select("*")
        .gte("timestamp", twoWeeksAgo.toISOString())
        .lt("timestamp", oneWeekAgo.toISOString());

      const currentWeekActivity = lastWeekActivity?.length || 0;
      const prevWeekActivityCount = prevWeekActivity?.length || 0;

      let partnerTrend = "+0%";
      if (prevWeekActivityCount > 0) {
        const percentageChange =
          ((currentWeekActivity - prevWeekActivityCount) /
            prevWeekActivityCount) *
          100;
        partnerTrend = `${percentageChange > 0 ? "+" : ""}${Math.round(percentageChange)}%`;
      } else if (currentWeekActivity > 0) {
        partnerTrend = "+100%";
      }

      setStats((prev) => ({
        ...prev,
        partners: partners?.length || 0,
        partnerClicks,
        partnerTrend,
        conversionRate,
      }));
    } catch (error) {
      console.error("Error fetching partner stats:", error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const { data: recentDownloads, error: downloadsError } = await supabase
        .from("template_downloads")
        .select(
          `
            id,
            user_email,
            downloaded_at,
            templates:template_id (
              title
            )
          `
        )
        .order("downloaded_at", { ascending: false })
        .limit(10);

      if (downloadsError) throw downloadsError;

      const { data: partnerActivity, error: activityError } = await supabase
        .from("activity_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(10);

      if (activityError) throw activityError;

      const activityItems: RecentActivity[] = [];

      recentDownloads?.forEach((download) => {
        const templateTitle = Array.isArray(download.templates)
          ? download.templates[0]?.title || "Template"
          : "Template";

        activityItems.push({
          id: download.id,
          action: "Downloaded",
          item: templateTitle,
          user: download.user_email || "Anonymous",
          time: formatTimeAgo(new Date(download.downloaded_at)),
          type: "template",
        });
      });

      partnerActivity?.forEach((activity) => {
        let action = "";
        let item = activity.partner_name;

        switch (activity.action) {
          case "view":
            action = "Viewed";
            break;
          case "website_click":
            action = "Clicked website of";
            break;
          case "intro_request":
            action = "Requested intro to";
            break;
          default:
            action = "Interacted with";
        }

        const userName = activity.user_email
          ? activity.user_email
          : activity.user_id
            ? `User (${activity.user_id.substring(0, 8)}...)`
            : "Anonymous User";

        activityItems.push({
          id: activity.id,
          action,
          item,
          user: userName,
          time: formatTimeAgo(new Date(activity.timestamp)),
          type: "partner",
        });
      });

      const sortedActivity = activityItems
        .sort((a, b) => {
          const getRealDate = (activity: RecentActivity) => {
            if (activity.type === "template") {
              const download = recentDownloads?.find(
                (d) => d.id === activity.id
              );
              return download ? new Date(download.downloaded_at).getTime() : 0;
            } else if (activity.type === "partner") {
              const partnerAct = partnerActivity?.find(
                (p) => p.id === activity.id
              );
              return partnerAct ? new Date(partnerAct.timestamp).getTime() : 0;
            }
            return 0;
          };

          return getRealDate(b) - getRealDate(a);
        })
        .slice(0, 10);

      setRecentActivity(sortedActivity);
    } catch (error) {
      console.error("Error fetching recent activity:", error);
    }
  };
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Loading dashboard data...
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 w-20 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Platform overview and key metrics
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Template Downloads
            </CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.templateDownloads}</div>
            <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              {stats.templateTrend} vs last week
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-sky-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Partner Engagement
            </CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.partnerClicks}</div>
            <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              {stats.partnerTrend} vs last week
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Interactions
            </CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.dealUsage}</div>
            <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              {stats.dealTrend} vs last week
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              View → Intro Rate
            </CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.conversionRate}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Partner page conversion
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Templates
            </CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.templates}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Active downloadable resources
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Vetted Partners
            </CardTitle>
            <Handshake className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.partners}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Active partner listings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Users
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.floor(stats.templateDownloads / 2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Based on template downloads
            </p>
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Card
            className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
            onClick={() => navigate("/admin/templates")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Manage Templates</h3>
                    <p className="text-sm text-muted-foreground">
                      Upload and organize resources
                    </p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
            onClick={() => navigate("/admin/partners")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-sky-500/10 group-hover:bg-sky-500/20 transition-colors">
                    <Handshake className="h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Manage Partners</h3>
                    <p className="text-sm text-muted-foreground">
                      Add and track partner activity
                    </p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </CardContent>
          </Card>

          {/* <Card
            className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
            onClick={() => navigate("/admin/analytics")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Calculator className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">View Analytics</h3>
                    <p className="text-sm text-muted-foreground">
                      Detailed platform analytics
                    </p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Recent Platform Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No recent activity
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        activity.type === "template"
                          ? "bg-emerald-500"
                          : activity.type === "partner"
                            ? "bg-sky-500"
                            : "bg-primary"
                      }`}
                    />
                    <div>
                      <p className="text-sm">
                        <span className="text-muted-foreground">
                          {activity.action}
                        </span>{" "}
                        <span className="font-medium">{activity.item}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.user}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {activity.time}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
